import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Input } from "ui-kit/PrimitiveControls/Input";
import Plus from '../../assets/plusWhite.svg'
import Arrow from '../../assets/ArrowBottom.svg'
import Edit from '../../assets/edit.svg'
import Trash from '../../assets/trash.svg'
import Modal from "./components/Modal";
import { InputText } from "primereact/inputtext";
import { useAuthors } from "components/NewRelease/hooks/useAuthors";
import { useNavigate } from "react-router";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import IntlStore from "stories/IntlStore";
import {FormattedMessage} from "react-intl";

export const Authors = () => {
  const [search, setSearch] = useState<string>('');
  const [authorName, setAuthorName] = useState<string>('');

  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>('1');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(10);
  const [showModal, setShowModal] = useState('');
  const [delError, setDelError] = useState('')
  const [first, setFirst] = useState<number>(0);
  const [value, setValue] = useState<string>('');
  const [editId, setEditId] = useState<string>('');
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  const { authors, total, fetching, creating, removeAuthor, createAuthor, upDateAuthor } = useAuthors(
    selectedStatus,
    currentPage,
    pageCount,
    search,
  );

  const [loading, setLoading] = useState(fetching);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const goToFirstPage = () => {
    const event = {
      first: 0,
      rows: pageCount,
      page: 0,
      pageCount: Math.ceil(total / pageCount),
    };
    onPageChange(event);
  };

  const handleDeleteClick = (id: string) => {
    setShowModal(id);
  };

  const handleClose = () => {
    setShowModal('');
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setCurrentPage(event.page + 1);
    setFirst(event.rows * event.page);
    setPageCount(event.rows);
  };

  useEffect(() => {
    goToFirstPage()
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setSearch(value);
    }, 500);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [value]);

  useEffect(() => {
    setLoading(fetching)
  }, [fetching])

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    // Safari < 14
    if ((mql as any).addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    (mql as any).addListener?.(onChange);
    return () => (mql as any).removeListener?.(onChange);
  }, []);

  const trimmedName = authorName.trim();
  const isNameValid = trimmedName.length >= 3 && trimmedName.length <= 30;

  const saveAuthor = async () => {
    if (!isNameValid) return;

    if (!editId) {
      await createAuthor(trimmedName);
    } else {
      await upDateAuthor(editId, { name: trimmedName });
    }

    closeAuthorModal();
    goToFirstPage();
  };

  const closeAuthorModal = () => {
          setModalOpen(false);
          setAuthorName('');
    setEditId('');
  }

  return (
    <Container>
      <HeadBox>
        <SearchWrapper>
          <Icon viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 2a8 8 0 105.293 14.293l4.707 4.707 1.414-1.414-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" />
          </Icon>
          <SearchInput
            name="search"
            value={value} onChange={(event) => setValue(event.target.value)}
            placeholder={
              IntlStore.intl.formatMessage({
                id: "Authors.Search",
                defaultMessage: "Search by authors"
              })
            }
          />
        </SearchWrapper>
        <AddBox onClick={() => setModalOpen(true)}>
          <Plus /> <span style={{ cursor: 'pointer' }}>
          <FormattedMessage
            id="Authors.CreateAuthor"
            defaultMessage="Create author"
          />
        </span>
        </AddBox>
      </HeadBox>
      <ScrollableList>
      <Content>
        <TabeBox>
          <Title>
            <FormattedMessage
              id="Authors.Author"
              defaultMessage="Author"
            /> <Arrow /></Title>
        </TabeBox>
        {loading ? <Loading>
          <ProgressSpinner />
        </Loading> : authors?.map((author, index) => (
          <AuthorsRow key={author.id}>
            <AuthorCard>
              <Name>{author.name}</Name>
            </AuthorCard>
            <EditBox>
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setAuthorName(author.name);
                  setEditId(author.id);
                  setModalOpen(true);
                }}
              >
                <Edit />
              </span>
              <span style={{ cursor: 'pointer' }} onClick={() => handleDeleteClick(String(author.id))}>
                <Trash />
              </span>
            </EditBox>
          </AuthorsRow>
        ))}
      </Content>
      </ScrollableList>
      <PaginatorBar>
        {!fetching && !!onPageChange && (
          <PaginatorWrapper>
            <Paginator
              first={first}
              rows={pageCount}
              totalRecords={total}
              rowsPerPageOptions={[5, 10, 20, 30]}
              onPageChange={onPageChange}
              pageLinkSize={isMobile ? 1 : 5}
              template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            />
          </PaginatorWrapper>
        )}
      </PaginatorBar>
      <Modal isOpen={isModalOpen} onClose={closeAuthorModal}>
        <ModalContent onClick={(e) => { e.stopPropagation() }}>
                <ModalTitle>
                  <FormattedMessage
                    id="Authors.Author"
                    defaultMessage="Author"
                  />
                </ModalTitle>
                <InputText
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  placeholder={IntlStore.intl.formatMessage({
                    id: "Authors.AuthorName",
                    defaultMessage: "Author name"
                  })}
                  style={{ width: '100%', marginTop: '10px' }}
                />

          <ModalActions>
            <CancelButton type="button" onClick={closeAuthorModal}>
              Cancel
            </CancelButton>
            <SaveButton
              type="button"
              disabled={!isNameValid || creating}
              onClick={() => {
                saveAuthor();
              }}
            >
              Save
            </SaveButton>
          </ModalActions>
        </ModalContent>
      </Modal>
      {showModal && (
        <ModalOverlay>
          <ModalBox>
            {delError ? <TextBox><span>
                <FormattedMessage
                  id="Authors.Text1"
                  defaultMessage="Author can’t be deleted while active releases remain."
                /></span><span>
                <FormattedMessage
                  id="Authors.Text2"
                  defaultMessage="Please remove or deactivate all"
                />
                </span><span>
                   <FormattedMessage
                     id="Authors.Text3"
                     defaultMessage="releases before trying again"
                   />
                </span></TextBox> :
              <TextBox>
                <FormattedMessage
                  id="Authors.Text4"
                  defaultMessage="Are you sure you want to delete the author?"
                />
              </TextBox>}
            <ButtonBox>
              {!delError && <NoBox onClick={handleClose}>
                <FormattedMessage
                  id="Authors.No"
                  defaultMessage="No"
                />
              </NoBox>}
              <YesBox
                onClick={() => {
                  if (delError) {
                    setDelError('');
                    setShowModal('');
                    return;
                  }
                  removeAuthor(showModal)
                    .then(() => {
                      setShowModal('');
                      navigate('/authors');
                    })
                    .catch((error) => {
                      setDelError(error.message || 'Something went wrong');
                    });
                }}
              >
                {delError ? (
                  <FormattedMessage
                    id="Authors.Ok"
                    defaultMessage="Ok"
                  />
                ) : (
                  <FormattedMessage
                    id="Authors.Yes"
                    defaultMessage="Yes"
                  />
                )}
              </YesBox>

            </ButtonBox>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px 23px;
  height: 100%;
  min-height: 0;
  gap: 14px;
  overflow: hidden; /* prevent MainLayout main scroll; only list should scroll */
`;

const HeadBox = styled.div`
  width:100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
`
const SearchWrapper = styled.div`
  position: relative;
  width: 333px;
  height: 43px;
  border-radius: 10px;
  border: 1px solid #ccc;

  /* Mobile: keep search narrower (~50%) so header fits better */
  @media (max-width: 768px) {
    width: 70%;
    min-width: 160px;
  }
  `;

const SearchInput = styled(Input)`
  height: 41px;
  width: 100%;
  padding-left: 20px; 
  border: none;
  outline: none;

  &:focus {
    border: none;
    box-shadow: none;
  }

  &:active {
    border: none;
    box-shadow: none;
  }
`;


const Icon = styled.svg`
  position: absolute;
  left: 10px;
  top: 23px;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  fill: #999;
  pointer-events: none;
`;


const AddBox = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;

  span{
    font-family: SF Pro Display;
    font-weight: 600;
    font-style: Semibold;
    font-size: 13px;
    leading-trim: NONE;
    line-height: 18px;
    letter-spacing: 0%;
    vertical-align: middle;
    color:#6777FF;
  }
`

const ScrollableList = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  position: relative;

  /* hide scrollbar while keeping scroll */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Content = styled.div``;

const TabeBox = styled.div`
  width: 100%;
  height: 39px;
  border-radius: 10px;
  background: #F6F6F6;
  display: flex;
  align-items: center;
  padding-left:21px
`

const Title = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #7C7C7C;
`

const AuthorCard = styled.div`
  display:flex;
  align-items: center;
  gap:30px;
  padding:11px 0 10px 18px ;
`;

const Name = styled.span`
  width:200px;
  font-family: SF Pro Display;
  font-weight: 400;
  font-style: Regular;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 15px;
  letter-spacing: 0;

`;

const TextLine = styled.span`
  width:210px;
  display: block;
  font-size: 0.95rem;
  color: #333;
`;

const AuthorsRow = styled.div`
  width:100%;
  height: 51px;
  display:flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ccc;

`
const EditBox = styled.div`
    display:flex;
    gap:7px;

`

const ModalContent = styled.div`
  padding: 18px 16px;
  background: #fff;
  border-radius: 10px;
`;
const ModalTitle = styled.span`
  font-family: SF Pro Display;
  font-weight: 500;
  font-style: Medium;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #000000;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
`;

const CancelButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #E0E0E0;
  background: #fff;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
`;

const SaveButton = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: none;
  background: #6777FF;
  color: #fff;
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index:9999;
`;

const ModalBox = styled.div`
  width: 520px;
  height: 139px;
  opacity: 1;
  border-radius: 10px;
  background: #FFFFFF;
  display:flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TextBox = styled.div`
  width:374px;
  display:flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: SF Pro Display;
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  line-height: 16px;
  color: #000000;
`;

const ButtonBox = styled.div`
  display:flex;
  margin-top: 10px;
  cursor:pointer;
`;

const NoBox = styled.div`
  width: 60px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
`;

const YesBox = styled.div`
  width: 60px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #6777FF;
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: #FFFFFF;
`;

const PaginatorBar = styled.div`
  flex: 0 0 auto;
  width: calc(100% + 46px);
  margin: 0 -23px; /* full-bleed (cancel Container padding) so paginator fits in one row */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);

  /* Match Releases behavior: keep rows-per-page dropdown on the right and prevent wrapping */
  .p-paginator {
    width: 100%;
    display: flex;
    justify-content: center;
    position: relative;
    box-sizing: border-box;
    /* reserve space for the rows-per-page dropdown so it doesn't push content to the next line */
    padding-right: 88px;
    flex-wrap: nowrap;
    align-items: center;
  }

  .p-paginator-rpp-options,
  .p-paginator .p-dropdown {
    position: absolute;
    right: 10px;
  }

  .p-paginator-pages {
    white-space: nowrap;
  }

  .p-paginator-first,
  .p-paginator-prev,
  .p-paginator-next,
  .p-paginator-last {
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    /* Mobile: make paginator controls slightly more compact to avoid wrapping */
    .p-paginator .p-paginator-pages .p-paginator-page,
    .p-paginator-first,
    .p-paginator-prev,
    .p-paginator-next,
    .p-paginator-last {
      min-width: 32px;
      height: 32px;
    }
  }
`;

const PaginatorWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
`;
